import { registerComponent, registerSystem } from "aframe";
import { FeedbackDelegate, GetRayCallback, InputComponent, InputSystem, inputSystemSchema, press, release } from "./common";



interface GamepadSystem extends InputSystem
{
	getRay : GetRayCallback;
}

interface GamepadComponent extends InputComponent
{
	system : GamepadSystem;
	buttons : number[];
	axes : number[];
	data : {
		axisThreshold : number;
		axisRange : number;
	}
}


registerSystem("gamepad", { 
	schema : inputSystemSchema,
	init(this : GamepadSystem)
	{
		this.isPrimaryInput = false;

		const handleGamepad = (event : GamepadEvent) => {
			
			const connected = (event.type == "gamepadconnected");
			this.isPrimaryInput = connected;

			if(connected)
			{
				this.layout = {};
				
				const buttons : string[] = [];
				const words = event.gamepad.id.split(/[\s\W]+/);
				if(words.includes("Xbox"))
					buttons.push(
						"A","B","X","Y",
						"LB","RB","LT","RT",
						"⧉","☰",
						"Ⓛ","Ⓡ",
						"ᐃ","ᐁ","ᐊ","ᐅ"
					);
				else if(words.includes("057e") && words.includes("2009")) //Switch pro
					buttons.push(
						"B", "A", "Y", "X",
						"L", "R", "ZL", "ZR", 
						"⊖", "⊕",
						"Ⓛ","Ⓡ",
						"ᐃ","ᐁ","ᐊ","ᐅ",
						"🏠", "📷"
					);
				for(let n = buttons.length; --n >= 0; )
					this.layout!["Button" + String(n) ] = buttons[n];
			}
			else
				delete this.layout;

			this.el.emit("primary-input-changed", this);
		};
		window.addEventListener("gamepadconnected", handleGamepad);
		window.addEventListener("gamepaddisconnected", handleGamepad);

		this.getRay = (o,d,s) =>
		{
			const camMat = this.el.camera.matrixWorld.elements;
			s.call(o, camMat[12], camMat[13], camMat[14]);
			s.call(d, -camMat[8], -camMat[9], -camMat[10]);
		};
	}
} );



const axisName = (index : number, direction : number) =>
{
	const base = Math.floor(index / 2);
	return "Stick" + String(base) + ((index % 2) ? ((direction > 0) ? "Down" : "Up") : ((direction > 0) ? "Right" : "Left"));
};


const axisValue = (raw : number, threshold : number, range : number) =>
{
	const sign = (raw < 0) ? -1 : 1;
	const value = (Math.abs(raw) - threshold) / (range - threshold);
	return sign * Math.max(0, Math.min(1, value));
};


registerComponent("gamepad", {

	multiple : true, //id is the gamepad index

	schema : {
		axisThreshold : { type : "number", default : 0.05 },
		axisRange : { type : "number", default : 0.99 }
	},

	init(this : GamepadComponent)
	{
		this.actions = {};
		this.buttons = [];
		this.axes = [];
		
	},

	feedback(this : GamepadComponent, intensity : number, milliseconds: number, action? : string)
	{
		const pad = navigator.getGamepads()[ Number(this.id || "0") ];
		if(!(pad && pad.connected))
			return;
		
		const ha = ( pad.hapticActuators || [] )[0] as ( GamepadHapticActuator & { pulse? : (k:number, d:number) => void });
		if(ha && ha.pulse)
			ha.pulse!(intensity, isNaN(milliseconds!) ? 100 : milliseconds);

		const va = (pad as (Gamepad & { vibrationActuator? : { playEffect? : (type : string, params : any) => void } } ) ).vibrationActuator;
		if(va && va.playEffect)
			va.playEffect("dual-rumble", { duration : isNaN(milliseconds!) ? 100 : milliseconds, weakMagnitude : intensity, strongMagnitude : intensity });
	},

	tick(this: GamepadComponent, time, timeDelta) {
		
		const pad = navigator.getGamepads()[ Number(this.id || "0") ];
		if(!( pad && pad.connected && document.hasFocus() ))
			return;

		const keyMap = this.system.data, buttons = pad.buttons, axes = pad.axes;
		for(let n=buttons.length; --n >= 0; )
		{
			const btn = buttons[n];
			const value = btn.pressed ? 1.0 : (btn.touched ? btn.value : -1.0);
			const previous = this.buttons[n] || -1.0;

			if(value == previous) continue;
			this.buttons[n] = value;

			const key = "Button" + n;
			let action = keyMap[key];
			if(!action) continue; //No action mapped.

			if(value > 0)
				press(this, action, key, value, this.system.getRay);
			else
				release(this, action, key);
		}

		for(let n=axes.length; --n >= 0; )
		{
			const value = axisValue( axes[n], this.data.axisThreshold, this.data.axisRange ), previous = this.axes[n] || 0.0;

			if(value == previous) continue;
			this.axes[n] = value;

			if((previous != 0) && ((value * previous) <= 0)) //Change of direction or back to zero
			{
				const key = axisName(n, previous);
				const action = keyMap[key];
				action && release(this, action, key);
			}

			if(value != 0)
			{
				const key = axisName(n, value);
				const action = keyMap[key];
				action && press(this, action, key, Math.abs(value), this.system.getRay);
			}
		}
	}

	
})




